import EhpadSubscriptionFields from "../../../components/EhpadSubscriptionFields";
import PropTypes from "prop-types";
import { useState } from "react";
import { API } from "../../../services/api";
import { ConfirmDeleteModal } from "../AdminCommon";
import { ehpadShape, tourneeShape } from "../adminShapes";
import { InvitationEmailField } from "./EhpadInvitationParts";
import { EhpadConnectionControls, EhpadIdentityFields } from "./EhpadEditorParts";
import { ehpadEditorForm, ehpadEditorValidationError, putEhpad, saveEhpadFollowUps } from "./saveEhpadEditor";
import { useEhpadConnectionActions } from "./useEhpadConnectionActions";
import { invitationEmailUnavailableMessage } from "./ehpadInvitations";
import { btnBlackStyle, btnOutlineDangerStyle, btnOutlineStyle, errorStyle } from "../styles/adminDashboardStyles";

export function EhpadEditor({ ehpad, tournees, pendingInvitation = null, connection = "none", deactivatedEmail = null, onInvited, onConnectionChanged = () => {}, onUpdated, onSaved, onDeleted, onCancel }) {
    const [form, setForm] = useState(() => ehpadEditorForm(ehpad));
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [inviteEmail, setInviteEmail] = useState("");
    const [subscription, setSubscription] = useState({ payload: null, error: null });
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const connected = connection === "connected";
    const actions = useEhpadConnectionActions({
        ehpad, connected, pendingInvitation, deactivatedEmail, inviteEmail, setInviteEmail, onInvited, onConnectionChanged, setError, setNotice,
    });
    const { confirmCancelInvitation, cancellingInvitation, setConfirmCancelInvitation, handleCancelInvitation } = actions;

    const handleDelete = async () => {
        setDeleting(true);
        setError(null);
        try {
            await API.delete(`/admin/ehpads/${ehpad.id}`);
            onDeleted(ehpad.id);
        } catch (err) {
            setError(err.response?.data?.message ?? "Erreur lors de la suppression.");
            setConfirmDelete(false);
        } finally {
            setDeleting(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setError(null);
        setNotice(null);
        const email = inviteEmail.trim();
        const invalid = ehpadEditorValidationError(form, email, subscription);
        if (invalid) {
            setError(invalid);
            return;
        }
        setSaving(true);
        if (email) {
            // Email déjà pris (compte portail ou invitation en cours pour un autre EHPAD) : rien n'est enregistré.
            const taken = await invitationEmailUnavailableMessage(email, ehpad.id);
            if (taken) {
                setError(taken);
                setSaving(false);
                return;
            }
        }
        try {
            const data = await putEhpad(ehpad, form, email);
            // La fiche est enregistrée : la ligne de la liste est rafraîchie tout de suite, quoi qu'il arrive ensuite.
            onUpdated(data);
            const { failed, report } = await saveEhpadFollowUps({
                ehpadId: ehpad.id, email, subscription, onInvited, onInvitationSent: () => setInviteEmail(""),
            });
            if (failed) {
                // L'éditeur reste ouvert pour afficher précisément ce qui a réussi et ce qui a échoué.
                setError(report.join(" "));
                setSaving(false);
                return;
            }
            onSaved(data);
        } catch (err) {
            setError(err.response?.data?.message ?? "Erreur lors de la sauvegarde.");
            setSaving(false);
        }
    };

    return (
        <>
            <div style={{ background: "#f8fafc", borderTop: "1px solid #e5e7eb", padding: "16px 20px" }}>
                <form onSubmit={handleSave} noValidate aria-label={`Modifier l'EHPAD ${ehpad.nom}`} style={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "flex-end" }}>
                    <EhpadIdentityFields ehpad={ehpad} tournees={tournees} form={form} setForm={setForm} />
                    <div style={{ flex: "1 1 100%" }}>
                        <InvitationEmailField
                            inputId={`admin-edit-ehpad-${ehpad.id}-invite-email`}
                            value={inviteEmail}
                            onChange={setInviteEmail}
                            submitLabel="Enregistrer"
                        >
                            <EhpadConnectionControls
                                connection={connection}
                                connected={connected}
                                pendingInvitation={pendingInvitation}
                                deactivatedEmail={deactivatedEmail}
                                saving={saving}
                                actions={actions}
                            />
                        </InvitationEmailField>
                    </div>
                    <div style={{ flex: "1 1 100%" }}>
                        <EhpadSubscriptionFields ehpad={ehpad} onChange={setSubscription} />
                    </div>
                    <div style={{ display: "flex", gap: "8px", flex: "1 1 100%" }}>
                        <button type="submit" disabled={saving || deleting} style={btnBlackStyle}>
                            {saving ? "Sauvegarde..." : "Enregistrer"}
                        </button>
                        <button type="button" onClick={onCancel} disabled={deleting} style={btnOutlineStyle}>
                            Annuler
                        </button>
                        <button type="button" onClick={() => setConfirmDelete(true)} disabled={saving || deleting} style={btnOutlineDangerStyle}>
                            Supprimer
                        </button>
                    </div>
                </form>
                {notice && <p style={{ margin: "10px 0 0", fontSize: "13px", color: "#15803d" }}>{notice}</p>}
                {error && <p style={errorStyle}>{error}</p>}
            </div>
            {confirmCancelInvitation && (connected || pendingInvitation) && (
                <ConfirmDeleteModal
                    title="Annuler le raccordement ?"
                    confirmLabel="Annuler le raccordement"
                    deleting={cancellingInvitation}
                    onCancel={() => !cancellingInvitation && setConfirmCancelInvitation(false)}
                    onConfirm={handleCancelInvitation}
                >
                    {connected ? (
                        <>
                            Le lien entre <strong>{ehpad.nom}</strong> et la pharmacie sera désactivé.
                            Rien n'est supprimé : vous pourrez le réactiver.
                        </>
                    ) : (
                        <>
                            Le lien et le code envoyés à <strong>{pendingInvitation.email}</strong> ne fonctionneront plus.
                            Vous pourrez renvoyer une invitation plus tard.
                        </>
                    )}
                </ConfirmDeleteModal>
            )}
            {confirmDelete && (
                <ConfirmDeleteModal
                    title="Supprimer l'EHPAD ?"
                    deleting={deleting}
                    onCancel={() => !deleting && setConfirmDelete(false)}
                    onConfirm={handleDelete}
                >
                    Êtes-vous sûr de vouloir supprimer <strong>{ehpad.nom}</strong> ? Cette action est irréversible.
                </ConfirmDeleteModal>
            )}
        </>
    );
}
EhpadEditor.propTypes = {
    ehpad: ehpadShape.isRequired,
    pendingInvitation: PropTypes.shape({
        email: PropTypes.string,
        blocked: PropTypes.bool,
        expired: PropTypes.bool,
    }),
    onUpdated: PropTypes.func.isRequired,
    onInvited: PropTypes.func.isRequired,
    connection: PropTypes.oneOf(["none", "manager", "connected", "deactivated"]),
    deactivatedEmail: PropTypes.string,
    onConnectionChanged: PropTypes.func,
    tournees: PropTypes.arrayOf(tourneeShape).isRequired,
    onSaved: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};
